// OptimalPace Brand Theme
export const colors = {
  // Primary brand colors
  doryLighterBlue: '#9CB0BD',
  doryBlue: '#405C73',
  doryDarkerBlue: '#3B515D',
  doryBeige: '#F2F2F2',
  doryGreen: '#435F60',
  doryBlack: '#121616',
  
  // Semantic colors
  primary: '#405C73',
  secondary: '#9CB0BD',
  accent: '#435F60',
  background: '#FFFFFF',
  surface: '#F2F2F2', // doryBeige — cards, charts, tracks against white page
  text: '#121616',
  textLight: '#3B515D',
  
  // Status colors
  success: '#435F60',
  warning: '#9CB0BD',
  error: '#3B515D',
  info: '#405C73',
  
  // Interactive states
  hover: '#3B515D',
  active: '#121616',
  disabled: '#9CB0BD',
  neutral: '#9CB0BD'
};

export const typography = {
  // Font families
  headerFont: "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  bodyFont: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  
  // Font weights
  weights: {
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700
  },
  
  // Font sizes (mobile-first)
  sizes: {
    // Headers
    h1: '2rem',      // 32px - scaled down from 44pt for mobile
    h2: '1.5rem',    // 24px - scaled down from 30pt for mobile  
    h3: '1.25rem',   // 20px - matches design
    
    // Body text
    body: '1rem',    // 16px - scaled down from 18pt for mobile
    bodyLarge: '1.125rem', // 18px - original design size
    small: '0.875rem',     // 14px - matches design
    tiny: '0.625rem',      // 10px - matches design
    
    // Mobile-specific
    touch: '1.125rem' // 18px - good for touch targets
  },
  
  // Line heights
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
    loose: 1.8
  },
  
  // Letter spacing
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.2em' // For uppercase text
  }
};

export const spacing = {
  // Base spacing unit
  base: '1rem', // 16px
  
  // Spacing scale
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem',     // 48px
  xxxl: '4rem',    // 64px
  
  // Component-specific spacing
  screenPadding: '1.5rem', // 24px - main content padding
  sectionGap: '2rem',      // 32px - gap between sections
  buttonPadding: '0.75rem 1.5rem', // 12px 24px
  inputPadding: '0.875rem 1rem'    // 14px 16px
};

export const borderRadius = {
  none: '0',
  sm: '0.25rem',   // 4px
  md: '0.5rem',    // 8px
  lg: '0.75rem',   // 12px
  xl: '1rem',      // 16px
  full: '9999px'   // Fully rounded
};

export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(18, 22, 22, 0.05)',
  md: '0 4px 6px -1px rgba(18, 22, 22, 0.1), 0 2px 4px -1px rgba(18, 22, 22, 0.06)',
  lg: '0 10px 15px -3px rgba(18, 22, 22, 0.1), 0 4px 6px -2px rgba(18, 22, 22, 0.05)',
  xl: '0 20px 25px -5px rgba(18, 22, 22, 0.1), 0 10px 10px -5px rgba(18, 22, 22, 0.04)'
};

export const breakpoints = {
  mobile: '320px',
  mobileLg: '480px',
  tablet: '768px',
  desktop: '1024px',
  desktopLg: '1200px'
};

export const zIndex = {
  base: 1,
  dropdown: 1000,
  sticky: 1020,
  modal: 1030,
  popover: 1040,
  tooltip: 1050,
  toast: 1060
};

// Animation durations and easings
export const animation = {
  durations: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms'
  },
  
  easings: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  }
};

// Complete theme object
const theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  breakpoints,
  zIndex,
  animation
};

export default theme; 